/**
 * V4LeftPanel — Phase 7q-1.
 *
 * Single source of left-side chrome in V4. Mirrors the V4RightRail
 * pattern: a thin icon strip on the panel's left edge picks which
 * sub-panel renders in the body. Only ONE active surface at a time.
 *
 *   ┌──┬───────────────────┐
 *   │📦│ Node palette       │   ← icon strip + panel body
 *   │📜│   or               │
 *   │  │ Runs list          │
 *   └──┴───────────────────┘
 *
 * Modes:
 *   • LEFT_MODES.NODES — V4NodePalette (drag-drop + click-to-add)
 *   • LEFT_MODES.RUNS  — V4RunsPanel (V3-parity execution tab)
 *
 * Gentle auto-mode: when openRunId arrives and the operator is still
 * in NODES mode, nudge to RUNS so the run they just kicked off appears
 * without an extra click. If the operator manually flipped back to
 * NODES while a run is active, we don't nag — auto-switch only fires
 * on the leading edge of openRunId, not every render.
 *
 * Zero regression: defaults to NODES mode. The 14k existing workflows
 * land on the palette exactly as before. Runs tab is opt-in by click
 * or by the openRunId leading-edge nudge.
 */

import React, { useEffect, useRef } from 'react';
import { Hash, History } from 'lucide-react';
import { S, BRAND, SPACING, RADIUS, FONT, WEIGHT } from './v4Tokens';

import V4NodePalette from './V4NodePalette';
import V4RunsPanel from './V4RunsPanel';

export const LEFT_MODES = Object.freeze({
  NODES: 'nodes',
  RUNS:  'runs',
});

const MODE_META = [
  { key: LEFT_MODES.NODES, icon: Hash,    label: 'Nodes' },
  { key: LEFT_MODES.RUNS,  icon: History, label: 'Runs' },
];

const ICON_STRIP_W = 36;

export default function V4LeftPanel({
  // Controlled mode — host owns it so other surfaces (Run button click,
  // Runs dropdown row click) can flip it.
  mode = LEFT_MODES.NODES,
  onModeChange,
  // Palette props (forwarded)
  onAddOrphan,
  hiddenTypes,
  // Runs panel props (forwarded)
  workflowId,
  freshRunId,
  openRunId,
  onSelectRun,
  onToast,
}) {
  // Leading-edge auto-nudge: when openRunId transitions from null →
  // truthy AND the operator is in NODES mode, flip to RUNS. We track
  // the previous openRunId to detect the transition rather than the
  // current value so toggling back to NODES sticks.
  const lastOpenRunIdRef = useRef(openRunId);
  useEffect(() => {
    const prev = lastOpenRunIdRef.current;
    lastOpenRunIdRef.current = openRunId;
    if (!openRunId) return;
    if (prev) return;                 // already-set; not a new run
    if (mode !== LEFT_MODES.NODES) return;
    if (typeof onModeChange === 'function') onModeChange(LEFT_MODES.RUNS);
  }, [openRunId, mode, onModeChange]);

  return (
    <div
      role="navigation"
      aria-label="Left panel"
      style={{
        display: 'flex', flexDirection: 'row',
        flexShrink: 0,
        background: '#ffffff',
        /* Right border lives on the inner sub-panel (V4NodePalette /
         * V4RunsPanel) so swapping modes doesn't double up. */
      }}
    >
      {/* Icon strip */}
      <div style={{
        width: ICON_STRIP_W,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: '6px 0',
        background: S[50],
        borderRight: `0.5px solid ${S[200]}`,
      }}>
        {MODE_META.map(({ key, icon: Icon, label }) => {
          const active = mode === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onModeChange && onModeChange(key)}
              aria-label={label}
              aria-pressed={active}
              title={label}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, padding: 0,
                color: active ? BRAND.icon : S[500],
                background: active ? BRAND.light : 'transparent',
                border: 'none',
                borderRadius: RADIUS.md,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'background 120ms ease',
              }}
            >
              <Icon size={14} aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {/* Body — renders one active sub-panel. Both are mounted-then-
          hidden via display:none so React state survives mode flips
          (preserves palette tab + search query when toggling to Runs
          and back). Keeps fetch-on-mount cheap because re-mount is
          avoided. */}
      <div style={{ display: mode === LEFT_MODES.NODES ? 'block' : 'none' }}>
        <V4NodePalette
          onAddOrphan={onAddOrphan}
          hiddenTypes={hiddenTypes}
        />
      </div>
      <div style={{ display: mode === LEFT_MODES.RUNS ? 'block' : 'none' }}>
        <V4RunsPanel
          workflowId={workflowId}
          freshRunId={freshRunId}
          openRunId={openRunId}
          onSelectRun={onSelectRun}
          onToast={onToast}
        />
      </div>
    </div>
  );
}
